import {
  ListenerPayload,
  OptimizelyContext,
  OptimizelyProvider,
  createInstance,
  enums,
} from '@optimizely/react-sdk';
import { PropsWithChildren, useContext, useEffect, useState } from 'react';
import {
  analyticsCookieExists,
  createAnalyticsCookie,
  getAnalyticsCookie,
} from '../utils/analyticsCookie';

import { LoadingPage } from '../pages/Loading/LoadingPage';
import config from '../config';
import { useAuthenticatedUser } from '../hooks/useAuthenticatedUser';
import { v4 } from 'uuid';
import { waitUntilHeapLoaded } from '../utils/heap';
import { isPageCreationLoginReturn } from '../utils/pageCreationLoginMarker';
import { replayBbidSignUpDecision } from './bbidSignUpExperiment';

export const optimizelyInstance = createInstance({
  sdkKey: config.optimizelySdkKey,
});

configureHeapIntegration();

// Module-level (not useRef): CustomOptimizelyProvider mounts once at the app
// root per page load, so this gives us "replay once per page load" semantics.
// A useRef would weaken that to "once per mount" and re-fire on any future
// remount (e.g. React strict-mode double-invocation).
let bbidSignUpDecisionReplayed = false;

export const CustomOptimizelyProvider = ({ children }: PropsWithChildren) => {
  const { accessToken } = useAuthenticatedUser();
  const [isReady, setIsReady] = useState(false);
  const userId = getUserId(accessToken?.guid);

  useEffect(() => {
    optimizelyInstance.onReady().then(() => {
      setIsReady(true);

      // Replay the BBID sign-up decision so the DECISION -> Heap listener tags
      // the user's variation (the assignment itself is made in SSO). Gate on the
      // pageCreationLogin marker so we only tag users SSO actually enrols;
      // replaying on every load tagged every visitor in Heap, not just sign-ups.
      if (
        !bbidSignUpDecisionReplayed &&
        isPageCreationLoginReturn(window.location.search)
      ) {
        const { loggedOutGuid } = getAnalyticsCookie();
        if (loggedOutGuid) {
          replayBbidSignUpDecision(optimizelyInstance, loggedOutGuid);
          bbidSignUpDecisionReplayed = true;
        }
      }
    });
  }, []);

  useEffect(() => {
    // Heap auto-generates a new anonymous user ID when its script re-initialises
    // (e.g. after returning from the BBID sign-up redirect), which breaks
    // cross-journey tracking. Identify Heap with the same stable GUID we use
    // for Optimizely so Heap aliases the new anonymous ID to our stable one.
    if (!userId) return;
    waitUntilHeapLoaded().then(() => {
      if (window.heap?.identity !== userId) {
        window.heap?.identify(userId);
      }
    });
  }, [userId]);

  return (
    <OptimizelyProvider
      optimizely={optimizelyInstance}
      user={{
        id: userId,
        attributes: {
          isTestUser: document.cookie.includes('ui-test'),
        },
      }}
    >
      {isReady ? children : <LoadingPage />}
    </OptimizelyProvider>
  );
};

export function useOptimizely() {
  return useContext(OptimizelyContext);
}

type DecisionListenerPayload = ListenerPayload & {
  decisionInfo: {
    flagKey: string;
    variationKey: string | null;
  };
};

function configureHeapIntegration() {
  // Listen for DECISION (not ACTIVATE) so we report the flag key (e.g.
  // `consumerghostbusters_bbid_migration_signup`) that's configured in the
  // Optimizely UI, rather than the auto-generated inner rule key that
  // ACTIVATE's `experiment.key` exposes. We deliberately do NOT gate on
  // `decisionEventDispatched`: the assignment for some experiments (e.g.
  // BBID sign-up) is made in an upstream SSO app, so the impression event
  // may have already been sent there. We still need to tag the user in Heap
  // with their variation here so segmentation works across both apps.
  optimizelyInstance.notificationCenter.addNotificationListener<
    DecisionListenerPayload
  >(enums.NOTIFICATION_TYPES.DECISION, payload => {
    const { flagKey, variationKey } = payload.decisionInfo;

    if (!flagKey || !variationKey) return;

    waitUntilHeapLoaded().then(() => {
      window.heap?.addUserProperties({
        [`experiment_${flagKey}`]: variationKey,
      });
    });
  });
}

function getUserId(loggedInUserId?: string) {
  if (!analyticsCookieExists()) {
    createAnalyticsCookie(loggedInUserId);
  }

  const { loggedOutGuid } = getAnalyticsCookie();

  return loggedOutGuid ?? v4();
}
